import { clsx, type ClassValue } from "clsx";
import { format, fromUnixTime } from "date-fns";
import { ko } from "date-fns/locale";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const isNotEmpty = (data: any) => {
  if (data === null || data === undefined) {
    return false;
  }

  if (typeof data === "string" && data.trim() === "") {
    return false;
  }

  if (Array.isArray(data) && data.length === 0) {
    return false;
  }

  if (typeof data === "object" && data instanceof Date) {
    return !isNaN(data.getTime());
  }

  if (typeof data === "object" && Object.keys(data).length === 0) {
    return false;
  }

  return true;
};

export const convertDate = (data: string | number) => {
  if (!isNotEmpty(data)) {
    return "-";
  }

  // Unix timestamp인 경우 formatDate 사용
  if (typeof data === "number") {
    return formatDate(data, "yyyy-MM-dd HH:mm:ss");
  }

  // ISO 문자열인 경우 - 한국 시간대 고려
  if (typeof data === "string") {
    try {
      const date = new Date(data);
      // 한국 시간대로 변환된 날짜시간 문자열 반환
      return date.toLocaleString("sv-SE").replace("T", " "); // YYYY-MM-DD HH:mm:ss 형식
    } catch {
      // 변환 실패 시 기존 방식으로 fallback
      return data.replace("T", " ").substring(0, 19);
    }
  }

  return "-";
};

export const getDate = (data: string | number) => {
  if (!isNotEmpty(data)) {
    return "-";
  }

  // Unix timestamp인 경우 formatDate 사용
  if (typeof data === "number") {
    return formatDate(data, "yyyy-MM-dd");
  }

  // ISO 문자열인 경우 - 한국 시간대 고려
  if (typeof data === "string") {
    try {
      const date = new Date(data);
      // 한국 시간대로 변환된 날짜 문자열 반환
      return date.toLocaleDateString("sv-SE"); // YYYY-MM-DD 형식
    } catch {
      // 변환 실패 시 기존 방식으로 fallback
      return data.split("T")[0];
    }
  }

  return "-";
};

export const formattedOnlyDate = (date: string) => {
  if (isNotEmpty(date)) {
    const dateObject = new Date(date);
    return `${dateObject.getFullYear()}-${dateObject.getMonth() + 1}-${dateObject.getDate()}`;
  } else {
    return date;
  }
};

export const subtractDays = (date: Date, days: number) => {
  const result = new Date(date);
  result.setDate(result.getDate() - days);
  return result;
};

export const subtractMonths = (date: Date, months: number) => {
  const result = new Date(date);
  result.setMonth(result.getMonth() - months);
  return result;
};

export const calculateDDay = (targetDate: string | number) => {
  const today = new Date();
  let target: Date;
  if (typeof targetDate === "number") {
    target = fromUnixTime(targetDate);
  } else {
    target = new Date(targetDate);
  }
  const todayKST = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const targetKST = new Date(
    target.getFullYear(),
    target.getMonth(),
    target.getDate()
  );

  const diffTime = targetKST.getTime() - todayKST.getTime();
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

export const debounce = (func: any, delay: number) => {
  let timeoutId: any;
  return (...args: any) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

export const formatDate = (
  timestamp: number,
  dateFormat: string = "yyyy년 M월 d일 H시 m분 s초"
) => {
  if (typeof timestamp === "number" && !isNaN(timestamp)) {
    try {
      const date = new Date(timestamp * 1000);

      const kstString = date.toLocaleString("ko-KR", {
        timeZone: "Asia/Seoul",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
        hour12: false,
      });

      const match = kstString.match(
        /(\d{4})\.\s*(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{1,2}):(\d{1,2}):(\d{1,2})/
      );

      if (!match) {
        return "-";
      }

      const [, year, month, day, hour, minute, second] = match;

      if (dateFormat === "yyyy년 M월 d일") {
        return `${year}년 ${parseInt(month)}월 ${parseInt(day)}일`;
      } else if (dateFormat === "yyyy년 M월 d일 H시 m분 s초") {
        return `${year}년 ${parseInt(month)}월 ${parseInt(day)}일 ${parseInt(hour)}시 ${parseInt(minute)}분 ${parseInt(second)}초`;
      } else {
        const kstDate = new Date(
          date.toLocaleString("en-US", { timeZone: "Asia/Seoul" })
        );
        return format(kstDate, dateFormat, { locale: ko });
      }
    } catch {
      return "-";
    }
  }
  return "-";
};

export const formatToPercentString = (value: number, fraction: number = 0) => {
  if (isNaN(value)) return "0%";

  const percent = value * 100;
  return `${percent.toLocaleString(undefined, {
    minimumFractionDigits: fraction,
    maximumFractionDigits: fraction,
  })}%`;
};

export function formatKoreanCurrencyUnits(
  num: number | bigint,
  unit: number = 2
): string {
  const unitList = [
    { name: "조", value: 1_0000_0000_0000 },
    { name: "억", value: 1_0000_0000 },
    { name: "만", value: 1_0000 },
    { name: "원", value: 1 },
  ];

  const n = Number(num);
  if (isNaN(n) || n < 0) return "0원";

  const parts: string[] = [];
  let remaining = n;

  let startIndex = unitList.findIndex((unit) => remaining >= unit.value);
  if (startIndex === -1) startIndex = unitList.length - 1;

  const selectedUnits = unitList.slice(startIndex, startIndex + unit);

  selectedUnits.forEach((unit) => {
    const amount = Math.floor(remaining / unit.value);
    if (amount > 0 || unit.name === "원") {
      parts.push(`${amount}${unit.name}`);
    }
    remaining %= unit.value;
  });

  return parts.length > 0 ? parts.join(" ") : "0원";
}

// 조/억/만 단위까지만 표시하고 끝에 " 원" 붙이기 (예: "1억 2,345만 원")
export function formatKoreanCurrencyWithWon(num: number | bigint): string {
  const unitList = [
    { name: "조", value: 1_0000_0000_0000 },
    { name: "억", value: 1_0000_0000 },
    { name: "만", value: 1_0000 },
  ];

  const n = Number(num);
  if (isNaN(n) || n < 0) return "0 원";
  if (n === 0) return "0 원";

  const parts: string[] = [];
  let remaining = n;

  for (const unit of unitList) {
    const amount = Math.floor(remaining / unit.value);
    if (amount > 0) {
      parts.push(`${amount.toLocaleString()}${unit.name}`);
      remaining %= unit.value;
    }
  }

  if (parts.length === 0) {
    // 만 원 미만인 경우
    return `${n.toLocaleString()} 원`;
  }

  return parts.join(" ") + " 원";
}

export function formatKoreanCurrencyFull(num: number | bigint): string {
  const units = [
    { name: "조", value: 1_0000_0000_0000 },
    { name: "억", value: 1_0000_0000 },
    { name: "만", value: 1_0000 },
  ];

  const parts: string[] = [];
  let remaining = Number(num);

  for (const unit of units) {
    const amount = Math.floor(remaining / unit.value);
    if (amount > 0) {
      parts.push(`${amount}${unit.name}`);
      remaining %= unit.value;
    }
  }

  if (remaining > 0 || parts.length === 0) {
    parts.push(`${remaining}원`);
  }

  return parts.join(" ");
}

export function formatDollar(num: number): string {
  const absNum = Math.abs(num);

  if (absNum >= 1_000_000_000_000) {
    return `${(num / 1_000_000_000_000).toFixed(2)}T`;
  } else if (absNum >= 1_000_000_000) {
    return `${(num / 1_000_000_000).toFixed(2)}B`;
  } else if (absNum >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(2)}M`;
  } else if (absNum >= 1_000) {
    return `${(num / 1_000).toFixed(2)}K`;
  } else {
    return num.toString();
  }
}
